import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediumArt } from "@/lib/api";
import { usePlayer } from "@/contexts/PlayerContext";
import { useAuth } from "@/contexts/AuthContext";
import { Radar as RadarIcon, Flame, Sparkles, TrendingUp, Megaphone, Play, Pause } from "lucide-react";
import FireBadge from "@/components/FireBadge";
import { gradientCssForArtist } from "@/lib/artistTheme";

const TrackTile = ({ track, queue, index, dataTestid }) => {
  const { current, playing, togglePlay, playTracks } = usePlayer();
  const isCurrent = current?.trackId === track.trackId;
  const isPlayingThis = isCurrent && playing;
  const onPlay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isCurrent) togglePlay();
    else playTracks(queue, index);
  };
  const themeKey = track.artistName || track.trackName || "track";
  return (
    <div
      data-testid={dataTestid}
      className="group relative overflow-hidden flex flex-col cursor-pointer"
      onClick={onPlay}
    >
      <div
        className="relative aspect-square overflow-hidden"
        style={track.artworkUrl100 ? undefined : { background: gradientCssForArtist(themeKey) }}
      >
        {track.artworkUrl100 ? (
          <img
            src={mediumArt(track.artworkUrl100)}
            alt={track.trackName}
            className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-display text-[100px] text-white drop-shadow-lg leading-none">
              {(track.trackName || "?")[0]}
            </span>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black via-black/70 to-transparent pointer-events-none" />

        {/* Source tag (promo / fresh / trending) — top-left */}
        {track._radarSection && (
          <span
            data-testid={`radar-tag-${track._radarSection}`}
            className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-black/60 backdrop-blur border border-white/15 text-[9px] uppercase tracking-[0.2em] font-black text-white"
          >
            {track._radarSection === "promoted" && <Megaphone size={9} />}
            {track._radarSection === "fresh" && <Sparkles size={9} />}
            {track._radarSection === "trending" && <Flame size={9} />}
            {track._radarSection}
          </span>
        )}

        {/* Trending fire */}
        <div className="absolute top-2 right-2">
          <FireBadge item={track} size="sm" />
        </div>

        {/* Play button */}
        <button
          data-testid={`radar-play-${track.trackId}`}
          onClick={onPlay}
          className="absolute bottom-2 right-2 w-11 h-11 rounded-full bg-vermilion text-white flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300 shadow-[0_8px_24px_rgba(255,46,0,0.45)]"
          type="button"
        >
          {isPlayingThis ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" className="ml-0.5" />}
        </button>

        {/* Bottom info overlay */}
        <div className="absolute inset-x-0 bottom-0 p-3 z-10">
          <p className="font-display font-black text-white text-base leading-tight uppercase tracking-tight truncate">
            {track.trackName}
          </p>
          <p className="text-[11px] text-ink-secondary mt-1 truncate">
            {track.artistName}
          </p>
        </div>
      </div>
    </div>
  );
};

const SectionStrip = ({ icon: Icon, kicker, title, subtitle, tracks, sectionKey }) => {
  if (!tracks || tracks.length === 0) return null;
  return (
    <section className="mb-16" data-testid={`radar-section-${sectionKey}`}>
      <div className="flex items-center gap-3 mb-5">
        <Icon size={18} className="text-vermilion" />
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-vermilion font-black mb-1">
            {kicker}
          </p>
          <h2 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight">{title}</h2>
          {subtitle && <p className="text-sm text-ink-secondary mt-1">{subtitle}</p>}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {tracks.map((t, i) => (
          <TrackTile
            key={`${t.trackId}-${i}`}
            track={t}
            queue={tracks}
            index={i}
            dataTestid={`radar-tile-${sectionKey}-${t.trackId || i}`}
          />
        ))}
      </div>
    </section>
  );
};

export default function RadarPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    api.get("/radar/sections")
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData({ promoted: [], fresh: [], trending: [] }); })
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, []);

  if (loading) {
    return <div className="px-4 sm:px-8 py-16 text-ink-secondary">Loading Radar…</div>;
  }

  const isEmpty = !data || (
    (data.promoted?.length || 0) === 0 &&
    (data.fresh?.length || 0) === 0 &&
    (data.trending?.length || 0) === 0
  );

  return (
    <div data-testid="radar-page" className="px-4 sm:px-8 py-10 lg:py-14 max-w-7xl mx-auto pb-32">
      {/* Hero */}
      <header className="mb-12">
        <div className="flex items-center gap-2 mb-3">
          <RadarIcon size={14} className="text-vermilion" />
          <span className="text-[10px] uppercase tracking-[0.28em] text-vermilion font-black">
            1o1sound · live discovery
          </span>
        </div>
        <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl font-black uppercase tracking-tighter leading-[0.9]">
          Hotspot
        </h1>
        <p className="text-ink-secondary text-base sm:text-lg mt-5 max-w-2xl">
          Tomorrow&apos;s hits, today. A live mix of paid promo spots, the freshest uploads, and
          what listeners are actually playing this week. Updated daily.
        </p>
        {user && (
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/promote"
              data-testid="radar-get-on-cta"
              className="inline-flex items-center gap-2 bg-vermilion hover:bg-vermilion-hover text-white font-black uppercase tracking-wider text-xs px-5 py-3 rounded-sm transition shadow-[0_8px_24px_rgba(255,46,0,0.35)]"
            >
              <Megaphone size={14} /> Get on Hotspot — Promote a track
            </Link>
            <Link
              to="/upload"
              className="inline-flex items-center gap-2 bg-white/[0.06] hover:bg-white/[0.12] border border-white/15 text-white font-black uppercase tracking-wider text-xs px-5 py-3 rounded-sm transition"
            >
              <Sparkles size={14} /> Upload a track
            </Link>
          </div>
        )}
      </header>

      {isEmpty ? (
        <div className="text-center py-24 bg-white/[0.02] border border-white/10 rounded-sm">
          <RadarIcon size={36} className="mx-auto text-white/25 mb-3" />
          <h3 className="font-display text-xl font-black uppercase mb-1">Hotspot is warming up</h3>
          <p className="text-ink-secondary text-sm max-w-md mx-auto">
            As artists upload tracks, run promos, and listeners discover new music, this page lights up.
            Check back tomorrow.
          </p>
        </div>
      ) : (
        <>
          <SectionStrip
            icon={Megaphone}
            kicker="paid this week"
            title="Promoted"
            subtitle="Artists who put their money where their music is"
            tracks={data.promoted}
            sectionKey="promoted"
          />
          <SectionStrip
            icon={Sparkles}
            kicker="last 30 days"
            title="Fresh Uploads"
            subtitle="New 1o1sound artists picking up steam"
            tracks={data.fresh}
            sectionKey="fresh"
          />
          <SectionStrip
            icon={TrendingUp}
            kicker="last 7 days"
            title="Trending"
            subtitle="What real listeners are spinning right now"
            tracks={data.trending}
            sectionKey="trending"
          />
        </>
      )}
    </div>
  );
}
